import React, { useEffect, useState } from "react";
import { fetchAnalytics } from "../lib/api";
import { getConsent } from "../legal/CookieConsent";

function Stat({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="glass rounded-3xl p-6">
      <p className="text-xs tracking-widest text-[#14b8a6]/80">{label}</p>
      <p className="text-5xl font-display grad-text mt-2">{value}</p>
      {sub && <p className="text-xs text-white/50 mt-2">{sub}</p>}
    </div>
  );
}

export function AnalyticsSection() {
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    // GDPR (Batch B): respect the cookie banner. If the user hasn't
    // opted in to analytics, we don't even hit the endpoint — the
    // section renders a small notice with a link to re-open the
    // preferences dialog.
    const prefs = getConsent();
    if (!prefs.analytics) {
      setBlocked(true);
      return;
    }
    fetchAnalytics().then(setData);
  }, []);

  if (blocked) {
    return (
      <section
        id="analytics"
        data-testid="analytics-blocked-consent"
        className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z"
      >
        <div className="glass rounded-3xl p-6 text-center">
          <p className="text-[12px] tracking-widest text-[#14b8a6]/90 font-bold">
            ANALYTICS DISABLED
          </p>
          <p className="mt-2 text-[12px] text-white/65 max-w-md mx-auto">
            We don't load this widget until you opt in to analytics
            cookies. Tap below to open the cookie preferences and
            allow analytics.
          </p>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("lh:cookie-prefs:open"))}
            data-testid="analytics-open-cookie-prefs"
            className="mt-3 px-3 py-1.5 rounded-lg text-[10px] tracking-widest bg-[#14b8a6] text-black font-bold hover:bg-[#10a39a]"
          >
            COOKIE PREFERENCES
          </button>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section id="analytics" className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z">
        <div className="loader-ring mx-auto" />
      </section>
    );
  }

  const max = Math.max(1, ...data.by_game.map((g) => g.players));

  return (
    <section
      id="analytics"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z border-t border-[#14b8a6]/20"
    >
      <div className="mb-10">
        <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
          NETWORK PULSE
        </span>
        <h2 className="text-4xl md:text-5xl font-display neon-text mt-5">ANALYTICS</h2>
        <p className="text-lg text-[#a3a3ac] mt-3">Live signal across the hub.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mb-10">
        <Stat label="PLAYERS" value={data.totals.players} testId="stat-players" />
        <Stat label="LEAGUES" value={data.totals.leagues} testId="stat-leagues" />
        <Stat label="LFG SIGNALS" value={data.totals.lfg_posts} testId="stat-lfg" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="glass rounded-3xl p-6 lg:col-span-2">
          <p className="text-xs tracking-widest text-[#14b8a6]">PLAYERS PER GAME</p>
          <div className="mt-5 space-y-3" data-testid="players-per-game-chart">
            {data.by_game.map((g) => (
              <div key={g.game} data-testid={`game-bar-${g.game.replace(/\s+/g, '-')}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{g.game}</span>
                  <span className="text-[#14b8a6]">{g.players}</span>
                </div>
                <div className="h-3 rounded-full bg-black/40 overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      width: `${(g.players / max) * 100}%`,
                      background: "linear-gradient(90deg, #14b8a6, #1e2a3a, #14b8a6)",
                      boxShadow: "0 0 12px rgba(20, 184, 166, 0.6)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-3xl p-6">
          <p className="text-xs tracking-widest text-[#14b8a6]">YOUR PULSE</p>
          {data.me ? (
            <div className="mt-4 space-y-3" data-testid="my-stats">
              <div className="flex justify-between"><span className="text-white/60">MMR</span><span className="font-bold">{data.me.elo}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Applications</span><span className="font-bold">{data.me.applications}</span></div>
              <div className="flex justify-between"><span className="text-white/60">LFG posts</span><span className="font-bold">{data.me.lfg_posts}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Plan</span><span className="font-bold text-[#14b8a6]">{data.me.plan.toUpperCase()}</span></div>
            </div>
          ) : (
            <p className="text-white/50 mt-4 text-sm">Sign in to see your stats.</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default AnalyticsSection;
